<template>
  <div v-if="!changingFilter">
    <div class="grid gap-7 md:grid-cols-2">
      <template v-for="(offer, index) of offers" :key="offer.car.id">
        <div
          v-if="shouldRenderGroupingHeader(index)"
          class="col-span-full text-left text-base font-semibold leading-tight transition duration-300"
        >
          {{ getGroupingHeaderForOffer?.(offer) }}
        </div>
        <VehiclesListItem
          :offer="offer"
          :start-date="startDate"
          :station-ids="
            station && station !== CUSTOM_LOCATIONS && station.id ? [station.id] : undefined
          "
          :insurance-case-provider="insuranceCaseProvider"
          @click="() => handleVehicleSelected(offer)"
        />
      </template>
    </div>

    <div v-if="isFetchingNextPage" class="mt-8 flex justify-center">
      <Spinner />
    </div>

    <EntityListExceptions
      :status="status"
      :error="error"
      :items-count="offers.length"
      :no-items-text="t('noCarsFound')"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, unref, watch } from 'vue';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import { useI18n } from 'vue-i18n';
import VehiclesListItem from './VehiclesListItem.vue';
import { useScrollPosition } from '@/hooks/use-scroll-position';
import { CUSTOM_LOCATIONS } from '@/constants/custom-locations.constant';
import type { BookingLocation } from '@/entities/bookings/booking.entity';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import { useOffers } from '@/queries/use-offers';
import { GetOffersSort, PricingType, type Offer, type VehicleType } from '@carvia/ros-client-types';
import type { CreateBookingProps } from '@/views/bookings/booking-create/components/use-create-booking-props';
import { Alert } from '@/services/alert.service';
import { calculateRentalDays } from '@/utils/calculate-rental-days';

const props = defineProps<{
  createBookingProps: CreateBookingProps;
  changingFilter?: boolean;
  onlyClosestToDeliveryLocation?: boolean;
}>();

const emit = defineEmits<{
  (event: 'selectOffer', offer: Offer): void;
}>();

const { t } = useI18n();
const {
  station,
  pickupLocation,
  dropoffLocation,
  startDate,
  endDate,
  categoryFilter,
  makeFilter,
  insuranceCase,
  insuranceCaseProvider,
  partner,
  longTerm,
  includeTransfers,
} = props.createBookingProps;

const { scrollPositionBottom } = useScrollPosition();

const {
  data: offersData,
  status,
  error,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
} = useOffers(
  computed(() => ({
    stationIds:
      includeTransfers.value || station.value === CUSTOM_LOCATIONS || !station.value
        ? undefined
        : [station.value.id],
    transferDeliveryLocation: locationToCoordinates(pickupLocation.value),
    transferReturnLocation: locationToCoordinates(dropoffLocation.value),
    startDate: startDate.value,
    endDate: endDate.value,
    categories: categoryFilter.value ? [categoryFilter.value] : undefined,
    makeIds: makeFilter.value ? [makeFilter.value] : undefined,
    insuranceCaseId: insuranceCase.value?.id,
    insuranceCaseProvider: insuranceCaseProvider.value ?? undefined,
    isAvailable: true,
    isGhostCar: false,
    ignoreOpeningHoursAndHolidays: true,
    onlyClosestToDeliveryLocation: props.onlyClosestToDeliveryLocation,
    reduceCarsToOnePerVehicleType: false,
    includeInternalStations: true,
    limit: 20,
    sort: [GetOffersSort.TOTAL_PRICE],
    partnerId: partner.value?.id,
    pricingType: longTerm.value ? PricingType.LONG_TERM : undefined,
  })),
  computed(() => !longTerm.value || calculateRentalDays(startDate.value, endDate.value) >= 30),
);

function locationToCoordinates(location: BookingLocation | null): [number, number] | undefined {
  if (!location) {
    return undefined;
  }
  if (location.locationType === BookingLocationType.STATION) {
    return location.station?.location?.coordinates;
  }
  return location.location?.coordinates
    ? [location.location?.coordinates?.longitude, location.location?.coordinates?.latitude]
    : undefined;
}

const offers = useFlattenPaginatedData(offersData);

const getGroupingHeaderForOffer = computed(() => {
  const insuranceProvider = insuranceCase.value?.insuranceProvider ?? insuranceCaseProvider.value;
  return insuranceProvider && ['volvo', 'jlr'].includes(insuranceProvider)
    ? (offer: Offer): string | null | undefined =>
        t(
          `groupHeader.${insuranceProvider}Categories.${offer.car.vehicleType?.[`${insuranceProvider}Category` as keyof VehicleType] ?? 'OTHER'}`,
        )
    : undefined;
});

const shouldRenderGroupingHeader = computed(() => (index: number): boolean => {
  if (!getGroupingHeaderForOffer.value) return false;
  const current = getGroupingHeaderForOffer.value?.(offers.value[index]);
  if (!current) return false;
  if (index === 0) return true;
  const previous = getGroupingHeaderForOffer.value?.(offers.value[index - 1]);
  return current !== previous;
});

watch(scrollPositionBottom, (distance) => {
  if (distance < 400 && unref(hasNextPage) && !unref(isFetchingNextPage)) {
    fetchNextPage();
  }
});

const handleVehicleSelected = async (offer: Offer) => {
  if (props.createBookingProps.insuranceCaseProvider.value) {
    await Alert.fire({
      text: t('insuranceCaseProviderWarning'),
      icon: 'error',
    });
    return;
  }
  emit('selectOffer', offer);
};
</script>

<i18n lang="json">
{
  "en": {
    "noCarsFound": "No cars found",
    "insuranceCaseProviderWarning": "You have to create an insurance case, before you can create an insurance case booking.",
    "groupHeader": {
      "volvoCategories": {
        "VOLVO_40": "(XC/C/V/S/E) 40 Tariff group: (always choose Volvo if available)",
        "VOLVO_60": "(XC/C/V/S/E) 60 Tariff group: (always choose Volvo if available)",
        "VOLVO_90": "(XC/C/V/S/E) 90 Tariff group: (always choose Volvo if available)",
        "OTHER": "Without Volvo tariff group"
      },
      "jlrCategories": {
        "GROUP_1": "Tariff group 1",
        "GROUP_2": "Tariff group 2",
        "GROUP_3": "Tariff group 3",
        "OTHER": "Without JLR tariff group"
      }
    }
  },
  "de": {
    "noCarsFound": "Keine Autos gefunden",
    "insuranceCaseProviderWarning": "Du musst einen Versicherungsfall erstellen, bevor du eine Versicherungsfallbuchung erstellen kannst.",
    "groupHeader": {
      "volvoCategories": {
        "VOLVO_40": "(XC/C/V/S/E) 40 Tarif Gruppe: (immer Volvo nehmen wenn verfügbar)",
        "VOLVO_60": "(XC/C/V/S/E) 60 Tarif Gruppe: (immer Volvo nehmen wenn verfügbar)",
        "VOLVO_90": "(XC/C/V/S/E) 90 Tarif Gruppe: (immer Volvo nehmen wenn verfügbar)",
        "OTHER": "Ohne Volvo Tarif Gruppe"
      },
      "jlrCategories": {
        "GROUP_1": "Tarifgruppe 1",
        "GROUP_2": "Tarifgruppe 2",
        "GROUP_3": "Tarifgruppe 3",
        "OTHER": "Ohne JLR Tarifgruppe"
      }
    }
  },
  "es": {
    "noCarsFound": "No se encontraron coches",
    "insuranceCaseProviderWarning": "Debe crear un siniestro antes de poder crear una reserva de siniestro.",
    "groupHeader": {
      "volvoCategories": {
        "VOLVO_40": "(XC/C/V/S/E) 40 Grupo tarifario: (elija Volvo siempre que haya disponible)",
        "VOLVO_60": "(XC/C/V/S/E) 60 Grupo tarifario: (elija Volvo siempre que haya disponible)",
        "VOLVO_90": "(XC/C/V/S/E) 90 Grupo tarifario: (elija Volvo siempre que haya disponible)",
        "OTHER": "Sin grupo tarifario Volvo"
      },
      "jlrCategories": {
        "GROUP_1": "Grupo tarifario 1",
        "GROUP_2": "Grupo tarifario 2",
        "GROUP_3": "Grupo tarifario 3",
        "OTHER": "Sin grupo tarifario JLR"
      }
    }
  }
}
</i18n>
